import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";
import zenRetreatImage from "@/assets/zen-retreat-adu.jpg";

const HowToUseADUBackyardRetreat = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-backyard-retreat" />
      <Navigation />
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight" style={{ color: '#E4683B' }}>
                  Transform Your Backyard into a Zen Retreat
                </h1>
                <p className="text-xl text-white leading-relaxed">
                  Create a zen-inspired ADU that brings balance, peace, and mindfulness into your daily life.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={openCalendlyModal}
                  >
                    📅 Book & Schedule
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="lg"
                    className="bg-white text-[#E4683B] hover:bg-white/90"
                    onClick={() => window.location.href = '/landing'}
                  >
                    📐 What fits my lot?
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={zenRetreatImage} 
                  alt="Zen retreat ADU interior with minimalist design" 
                  className="rounded-lg shadow-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Benefits You'll Love
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Reduces Stress</h3>
                  <p className="text-muted-foreground">Daily meditation space that promotes mental wellness and stress relief right in your backyard.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Enhanced Wellness</h3>
                  <p className="text-muted-foreground">Dedicated space for wellness routines, yoga, and mindfulness practices.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Boosts Home Value</h3>
                  <p className="text-muted-foreground">Mindful design increases property appeal and resale value with unique lifestyle amenity.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Key Design Features */}
        <section className="py-16 px-4 bg-muted/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
              Key Design Features
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Minimalist Interiors</h3>
                <p className="text-muted-foreground">Natural materials, indoor-outdoor flow, and clean lines create a serene environment.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Zen Amenities</h3>
                <p className="text-muted-foreground">Soft lighting, cozy floor cushions, and dedicated tea nook for mindful moments.</p>
              </div>
            </div>
            <div className="mt-12">
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/price-roi-report', '_blank')}
              >
                💰 Get Price & ROI Report
              </Button>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Why Choose a Backyard ADU Retreat
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Avoid Rent or Resort Fees</h3>
                <p className="text-muted-foreground">Skip expensive wellness retreats - create your own sanctuary at home.</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Privacy Without Travel</h3>
                <p className="text-muted-foreground">Enjoy complete privacy and tranquility just steps from your main home.</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Increased Property Appeal</h3>
                <p className="text-muted-foreground">Unique wellness amenity boosts both lifestyle value and ROI potential.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-4 bg-primary/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8 text-white">
              Rediscover your focus. Escape the noise. Make space for stories and stillness.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('/our-work', '_blank')}
              >
                🏡 Our Portfolio
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/faq', '_blank')}
              >
                ❓ Our FAQ
              </Button>
            </div>
          </div>
        </section>

        {/* Calendly Modal */}
        {showCalendly && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
              <button 
                onClick={closeCalendlyModal} 
                className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
              <iframe 
                src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
                width="100%" 
                height="100%" 
                frameBorder="0" 
                title="Schedule a meeting" 
              />
            </div>
          </div>
        )}
        
        <Footer />
        <FloatingADUWizard />
      </div>
  );
};

export default HowToUseADUBackyardRetreat;